import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { Config } from '@leverege/plugin';
import { useDispatch } from 'react-redux';
import { Button, Dialog, Flex, FileInput, Label, Pane, Text, PropertyGrid, Checkbox, Content, TableV2 } from '@leverege/ui-elements';
import I18N from '@leverege/i18n';
import classnames from 'classnames';
import { v4 } from '@leverege/base62-util';
import equals from 'fast-deep-equal';
import { Trans } from 'react-i18next';
import exportUtil from "../../../../forms/file/Util.js";
import utils from "../../../../forms/editors/ImagineImageEditor/fileUtils.js";
import S from './ImageListEditor.css';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
const {
  t,
  tIcon
} = I18N.ns('molten.imagine.attributes.resourceImage.ImageListEditor');
const opts = {
  table: {
    isRenderer: true,
    blockChildren: false
  },
  detail: {
    isRenderer: true,
    blockChildren: false
  }
};
const cols = [{
  id: 'fileName',
  get Header() {
    return t('fileName');
  },
  minWidth: 160,
  accessor: rowObj => {
    return rowObj.name;
  }
}, {
  id: 'error',
  get Header() {
    return t('error');
  },
  minWidth: 160,
  accessor: rowObj => {
    return t('tooBig');
  }
}, {
  id: 'size',
  get Header() {
    return t('size');
  },
  minWidth: 160,
  accessor: rowObj => {
    return t('mb', {
      size: Math.round(rowObj.size / 100000) / 10
    });
  }
}];
export default {
  valueType: 'resourceImage@',
  getOptions: usage => {
    return Config.get('ResourceImageEditor', usage, opts[usage]);
  },
  create: opts => {
    const {
      attribute,
      children,
      context,
      editable,
      eventData,
      object,
      onChange,
      usage,
      value,
      key,
      dataEditable,
      ...options
    } = opts;
    const delegate = context?.clientProps?.delegate;
    const imagePathRoot = attribute.blueprint.attribute.getTagStartingWith('resourceImagePrefix:', true) || `resourceImage/${attribute?.blueprint?.updatePath.slice(5)}`;
    if (!editable) {
      return children;
    }
    return /*#__PURE__*/_jsx(ImageListToggle, {
      usage: usage,
      delegate: delegate,
      attr: attribute,
      eventData: eventData,
      target: object,
      value: value,
      onChange: onChange,
      imagePathRoot: imagePathRoot,
      ...options
    }, key);
  }
};
function ImageListToggle(props) {
  const {
    usage,
    value,
    delegate,
    target,
    imagePathRoot
  } = props;
  const dispatch = useDispatch();
  const [opened, setOpened] = useState(false);
  const [displayImage, setDisplayImage] = useState(false);
  const onClose = useCallback(() => {
    setOpened(false);
  }, []);
  const [tValue, imageKeys] = useMemo(() => {
    const tValue = value?.value ?? value ?? {};
    return [tValue, Object.keys(tValue)];
  }, [value]);
  const [viewIndex, setViewIndex] = useState(0);
  const changeViewIndex = useCallback((index, change) => {
    if (change === 'inc') {
      if (index + 1 < imageKeys.length) {
        setViewIndex(index + 1);
      } else {
        setViewIndex(0);
      }
    } else if (change === 'dec') {
      if (index - 1 >= 0) {
        setViewIndex(index - 1);
      } else {
        setViewIndex(imageKeys.length - 1);
      }
    }
  }, [imageKeys]);
  const imagesCount = Object.keys(tValue).length;
  useEffect(() => {
    // here
    const key = imageKeys[viewIndex];
    if (value?.additions?.[key]) {
      setDisplayImage(URL.createObjectURL(value?.additions[key]));
    } else if (target?.id && key) {
      const itemDelegate = delegate.item ? delegate.item(target.id) : delegate;
      dispatch(itemDelegate.resource(`${imagePathRoot}/${key}`).get()).then(result => {
        const {
          data: {
            url
          }
        } = result;
        setDisplayImage(url);
      });
    } else if (!key) {
      setDisplayImage(false);
    }
  }, [tValue, viewIndex]);
  if (usage === 'table' || usage === 'detail') {
    if (opened) {
      return /*#__PURE__*/_jsx(ImageListEditor, {
        ...props,
        opened: opened,
        onClose: onClose
      });
    }
    return /*#__PURE__*/_jsx(Button, {
      variant: "editImage",
      onClick: () => {
        setOpened(true);
      }
    });
  }
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsxs(Pane, {
      variant: "imageEditor|editor",
      layout: "flex:colS",
      children: [/*#__PURE__*/_jsxs(Flex, {
        variant: "rowM",
        children: [displayImage && /*#__PURE__*/_jsxs("div", {
          className: S.parent,
          children: [/*#__PURE__*/_jsx("img", {
            src: displayImage,
            alt: "",
            className: S.displayImage
          }), /*#__PURE__*/_jsx(Button, {
            icon: "fa-solid fa-chevron-left",
            className: classnames(S.child, S.common, S.win, S.left),
            onClick: () => changeViewIndex(viewIndex, 'dec')
          }), /*#__PURE__*/_jsx(Text, {
            className: classnames(S.child, S.common, S.count),
            children: imageKeys.length
          }), /*#__PURE__*/_jsx(Button, {
            icon: "fa-solid fa-chevron-right",
            className: classnames(S.child, S.common, S.win, S.right),
            onClick: () => changeViewIndex(viewIndex, 'inc')
          })]
        }), /*#__PURE__*/_jsxs(Flex, {
          variant: "colS",
          children: [imagesCount > 0 && /*#__PURE__*/_jsx(Label, {
            variant: "captionSmallBold",
            children: t('imagesCount', {
              count: imagesCount
            })
          }), /*#__PURE__*/_jsx(Button, {
            variant: "uploadFile",
            icon: imagesCount > 0 ? 'fa fa-pencil fa-fw' : 'fa-regular fa-images',
            onClick: () => setOpened(true),
            children: t('buttonText', {
              count: imagesCount
            })
          })]
        })]
      }), /*#__PURE__*/_jsx(Label, {
        variant: "helperText",
        children: t('caption')
      })]
    }), /*#__PURE__*/_jsx(ImageListEditor, {
      ...props,
      opened: opened,
      onClose: onClose
    })]
  });
}
function ImageListEditor(props) {
  const {
    eventData,
    target,
    value: pValue,
    delegate,
    onChange,
    disabled,
    onClose,
    opened,
    imagePathRoot
  } = props;
  const [value, setValue] = useState(pValue?.value ?? pValue ?? {});
  const [files, setFiles] = useState({});
  const [selections, setSelections] = useState({});
  const [images, setImages] = useState({});
  const [addImageErrors, setAddImageErrors] = useState([]);
  const [addImageAttempts, setAddImageAttempts] = useState([]);
  const [showImageErrors, setShowImageErrors] = useState(false);
  const dispatch = useDispatch();
  const onSubmit = useCallback(evt => {
    onChange({
      data: evt.data,
      value: {
        imagePathRoot,
        additions: files,
        removals: pValue ? Object.keys(pValue?.value ?? pValue).reduce((acc, cur) => {
          if (!value[cur]) {
            acc.push(cur);
          }
          return acc;
        }, []) : [],
        value
      }
    });
    onClose();
  }, [onChange, images, value]);
  const addImages = ({
    value: pFiles
  }) => {
    const newValue = {
      ...value
    };
    const newFiles = {
      ...files
    };
    const newImages = {
      ...images
    };
    const errors = [];
    for (let i = 0; i < pFiles.length; i++) {
      if (utils.checkFileSize(pFiles[i], 2 * 1000 * 1000) !== true) {
        errors.push(pFiles[i]);
        // accumulate errors,
        // Toast.error( t( 'fileTooBig', { fileName : pFiles[i].name, maxSize : '2 MB' } ) )
      } else {
        const id = v4();
        newValue[id] = Date.now();
        newFiles[id] = pFiles[i];
        newImages[id] = URL.createObjectURL(pFiles[i]);
      }
    }
    // set errors to state
    setValue(newValue);
    setFiles(newFiles);
    setImages(newImages);
    setAddImageErrors(errors);
    if (errors.length > 0) {
      setShowImageErrors(true);
      setAddImageAttempts(pFiles.length);
    }
  };
  const getImage = (id, loading, setLoading) => {
    if (!loading && !images[id]) {
      setLoading(true);
      const itemDelegate = delegate.item ? delegate.item(target.id) : delegate;
      dispatch(itemDelegate.resource(`${imagePathRoot}/${id}`).get()).then(result => {
        const {
          data: {
            url
          }
        } = result;
        const pImages = {
          ...images
        };
        pImages[id] = url;
        setImages(pImages);
        setLoading(false);
      });
    }
  };
  const allSelected = Object.keys(selections).length === Object.keys(images).length;
  const someSelected = useMemo(() => {
    return Object.keys(selections).filter(a => selections[a]).length > 0;
  }, [selections]);
  const [additions, removals] = useMemo(() => {
    const temp = pValue?.value ?? pValue ?? {};
    let additions = 0;
    let removals = 0;
    const pKeys = Object.keys(temp);
    const keys = Object.keys(value);
    for (let i = 0; i < pKeys.length; i++) {
      if (!value[pKeys[i]]) {
        removals++;
      }
    }
    for (let i = 0; i < keys.length; i++) {
      if (!temp[keys[i]]) {
        additions++;
      }
    }
    return [additions, removals];
  }, [value, pValue]);
  const revert = useCallback(() => {
    setValue(pValue?.value ?? pValue ?? {});
    setFiles({});
    setSelections({});
  }, [pValue]);
  const onCancel = useCallback(() => {
    onClose();
    revert();
  }, [onClose, revert]);
  const remove = useCallback(() => {
    const nValue = {
      ...value
    };
    const nFiles = {
      ...files
    };
    const keys = Object.keys(selections);
    for (let i = 0; i < keys.length; i++) {
      delete nValue[keys[i]];
      delete nFiles[keys[i]];
    }
    setValue(nValue);
    setFiles(nFiles);
    setSelections({});
  }, [value, files, selections]);
  const imageSelection = useCallback(({
    data,
    value
  }) => {
    const pSelections = {
      ...selections,
      [data]: value
    };
    setSelections(pSelections);
  }, [selections]);
  const submitDisabled = useMemo(() => {
    return equals(value, pValue);
  }, [value]);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(Dialog, {
      bodyClassName: classnames(S.dialogBody, S.really, S.win),
      contentVariant: "imageListEditor",
      title: /*#__PURE__*/_jsx(Flex, {
        variant: "rowSpaceBetween",
        children: /*#__PURE__*/_jsx(Text, {
          icon: "https://storage.googleapis.com/molten-ui-assets/screen-icons/icon-multi-image-title.svg",
          variant: "dialogTitle",
          children: t('dialogTitle')
        })
      }),
      footer: /*#__PURE__*/_jsxs(Flex, {
        align: "center",
        justify: "flex-end",
        variant: "rowL",
        children: [/*#__PURE__*/_jsx(Button, {
          onClick: onCancel,
          variant: "secondary",
          children: t('cancel')
        }), /*#__PURE__*/_jsx(Button, {
          disabled: submitDisabled,
          onClick: onSubmit,
          eventData: eventData,
          variant: "primary",
          children: t('submit')
        })]
      }),
      show: opened,
      portalTargetId: "moltenPortalRoot",
      children: /*#__PURE__*/_jsxs(Content, {
        className: classnames(S.dialogArea),
        children: [Object.keys(value).length === 0 ? /*#__PURE__*/_jsx(NoImages, {
          disabled: disabled,
          eventData: eventData,
          addImages: addImages
        }) : /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsxs(Content.Header, {
            className: S.header,
            layout: "flex:rowSpaceBetweenCenter",
            children: [/*#__PURE__*/_jsx(Checkbox, {
              label: t('selectAll'),
              variant: "small",
              value: allSelected,
              onChange: evt => {
                if (allSelected) {
                  setSelections({});
                } else {
                  const pSelections = {};
                  Object.keys(value).forEach(id => {
                    pSelections[id] = true;
                  });
                  setSelections(pSelections);
                }
              }
            }), someSelected ? /*#__PURE__*/_jsx(Button, {
              icon: "fa-regular fa-circle-minus",
              variant: "secondarySmallLeftIcon",
              onClick: remove,
              children: t('removeImages', {
                count: Object.keys(selections).length
              })
            }) : /*#__PURE__*/_jsx(FileInput, {
              disabled: disabled,
              variant: "secondarySmallLeftIcon",
              eventData: eventData,
              onChange: addImages,
              fileTypes: "image/*",
              icon: "fa fa-plus",
              multiple: true,
              children: t('addImages')
            })]
          }), /*#__PURE__*/_jsx(Content.Area, {
            children: /*#__PURE__*/_jsx(PropertyGrid, {
              variant: "imagesListEditor",
              columns: 4,
              vertical: false,
              inline: false,
              children: Object.keys(value).map((v, i) => {
                return /*#__PURE__*/_jsx(PropertyGrid.Item, {
                  children: /*#__PURE__*/_jsx(ImageTile, {
                    value: v,
                    src: images[v],
                    selected: selections[v],
                    onChange: imageSelection,
                    getImage: getImage
                  })
                }, v);
              })
            })
          })]
        }), (additions > 0 || removals > 0) && /*#__PURE__*/_jsxs(Content.Footer, {
          style: {
            margin: '16px',
            backgroundColor: 'var( --color-base025 )',
            border: '1px solid var( --color-base200 )',
            padding: 'var(--size-pad12 ) var( --size-padLarge )',
            borderRadius: '4px'
          },
          layout: "flex:rowSpaceBetweenCenter",
          children: [/*#__PURE__*/_jsxs(Text, {
            children: [t('additions', {
              count: additions
            }), additions > 0 && removals > 0 ? t('and') : '', t('removals', {
              count: removals
            })]
          }), /*#__PURE__*/_jsx(Button, {
            variant: "revert",
            onClick: revert,
            children: t('revert')
          })]
        })]
      })
    }), /*#__PURE__*/_jsx(AttachImageErrors, {
      attempts: addImageAttempts,
      show: showImageErrors,
      onClose: () => {
        setShowImageErrors(false);
      },
      errors: addImageErrors
    })]
  });
}
function NoImages(props) {
  const {
    disabled,
    eventData,
    addImages
  } = props;
  return /*#__PURE__*/_jsx(Content.Area, {
    children: /*#__PURE__*/_jsxs(Flex, {
      style: {
        height: '100%',
        width: '100%'
      },
      align: "center",
      justify: "center",
      children: [/*#__PURE__*/_jsx("img", {
        style: {
          height: '200px',
          width: '200px'
        },
        src: "https://storage.googleapis.com/molten-ui-assets/screen-icons/no-images-editor.svg",
        alt: "No Images"
      }), /*#__PURE__*/_jsxs(Flex, {
        variant: "colM",
        children: [/*#__PURE__*/_jsx(Text, {
          variant: "subtitle",
          children: t('noImagesTitle')
        }), /*#__PURE__*/_jsx(Text, {
          variant: "body",
          children: t('noImagesBody')
        }), /*#__PURE__*/_jsx(FileInput, {
          style: {
            width: '142px'
          },
          disabled: disabled,
          variant: "primarySmall",
          eventData: eventData,
          onChange: addImages,
          fileTypes: "image/*",
          icon: "fa fa-plus",
          multiple: true,
          children: t('addImages')
        })]
      })]
    })
  });
}
function ImageTile(props) {
  const {
    value,
    src,
    selected,
    onChange,
    getImage
  } = props;
  const [imageEl, setImageEl] = useState();
  const [loading, setLoading] = useState();
  const ref = useCallback(node => {
    if (node) {
      setImageEl(node);
    }
  }, []);
  const handleIntersection = useCallback(([entry]) => {
    if (entry.isIntersecting) {
      getImage(value, loading, setLoading);
    }
  }, [getImage]);
  useEffect(() => {
    const observer = new IntersectionObserver(handleIntersection);
    if (imageEl) {
      observer.observe(imageEl);
    }
    return () => {
      observer.disconnect();
    };
  }, [imageEl, handleIntersection]);
  return /*#__PURE__*/_jsxs(Pane, {
    className: S.imageContainer,
    children: [/*#__PURE__*/_jsx("img", {
      ref: ref,
      className: S.image,
      src: src,
      alt: value
    }), /*#__PURE__*/_jsx(Checkbox, {
      className: S.imageCheck,
      variant: "small",
      value: selected,
      eventData: value,
      onChange: onChange
    })]
  });
}
function AttachImageErrors(props) {
  const {
    errors,
    onClose,
    show,
    attempts
  } = props;
  return /*#__PURE__*/_jsx(Dialog, {
    headerVariant: "dialogTitle",
    title: /*#__PURE__*/_jsx(Flex, {
      variant: "rowSpaceBetween",
      children: /*#__PURE__*/_jsx(Text, {
        icon: /*#__PURE__*/_jsx("i", {
          className: "fa-kit fa-lv-warning-filled",
          style: {
            color: 'var( --color-danger500 )'
          }
        }),
        variant: "dialogTitle",
        children: t('dialogErrorTitle')
      })
    }),
    footer: /*#__PURE__*/_jsx(Flex, {
      align: "center",
      justify: "flex-end",
      variant: "rowL",
      children: /*#__PURE__*/_jsx(Button, {
        onClick: onClose,
        variant: "primary",
        children: t('done')
      })
    }),
    show: show,
    children: /*#__PURE__*/_jsxs(Pane, {
      style: {
        padding: 'var( --size-padLarge )'
      },
      layout: "flex:colL",
      children: [/*#__PURE__*/_jsx(Text, {
        variant: "body",
        children: /*#__PURE__*/_jsx(Trans, {
          i18nKey: "molten.imagine.attributes.resourceImage.ImageListEditor.uploadsFailed",
          values: {
            successes: attempts - errors.length,
            errors: errors.length
          },
          components: {
            r: /*#__PURE__*/_jsx("span", {
              style: {
                color: 'var( --color-danger700'
              }
            })
          }
        })
      }), /*#__PURE__*/_jsx(Button, {
        style: {
          width: 'fit-content'
        },
        variant: "linkNoPad",
        onClick: () => {
          exportUtil.exportCSV(errors, cols, 'imageUploadErrors.csv');
        },
        icon: tIcon('exportIcon', 'fa fa-fw fa-download'),
        children: t('downloadErrors')
      })]
    })
  });
}